// A campaign's conversations, on its Creators tab: one thread with each
// creator who joined, the one that needs an answer first. Each creator shows
// as they do everywhere else (apps/shared/person): their picture, first name,
// TikTok handle and where they are with the work. A click on them, or on View
// profile, opens their profile. The newest message sits at the bottom, in
// view. The box to write in starts at one line and grows with the text up to
// six, so the conversation keeps the room (Brúnó, 2026-10-15). Enter sends and
// Shift+Enter starts a new line. On a touch screen, Enter is a new line and the
// button sends. On a phone the list and the conversation take turns.

import {useEffect, useLayoutEffect, useRef, useState} from 'react';
import type {ChangeEvent, FormEvent, KeyboardEvent} from 'react';
import type {BrandCampaign, BrandParticipant} from '../../shared/brand-api';
import type {StoredFile} from '../../shared/creator-api';
import {Icon} from '../creator/components/Icon';
import {Person} from '../shared/person/Person';
import {creatorPicture, describe, markRead, messageFileUrl, removeCampaignCreator, sendMessage, uploadMessageFile} from './api';
import {useCreatorProfile} from './creator-profile';
import {participantStatus} from './format';
import {RemoveCreatorDialog} from './remove-creator';
import {useBrand} from './store';
import './threads.css';

/** What the off-platform guard did with a brand's message, as the brand sees it in the thread. */
const GUARD_LABEL = {held: 'Held for review', not_delivered: 'Not delivered: it moved the collaboration off Earn'} as const;
const HELD_TOAST = 'Held for review. Our team checks it before the creator sees it, usually within a few hours.';
const MAX_ATTACHMENTS = 5;
const MAX_FILE_BYTES = 100 * 1024 * 1024;
const IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/avif']);

function humanSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(bytes < 10 * 1024 * 1024 ? 1 : 0)} MB`;
}

function isImage(contentType: string): boolean {
  return IMAGE_TYPES.has((contentType || '').split(';')[0]!.trim().toLowerCase());
}
/** Where the portal turns into its phone layout (styles.css): the list and the conversation take turns. */
const NARROW = '(max-width: 899px)';

function useNarrow(): boolean {
  const [narrow, setNarrow] = useState(() => window.matchMedia(NARROW).matches);
  useEffect(() => {
    const query = window.matchMedia(NARROW);
    const change = () => setNarrow(query.matches);
    query.addEventListener('change', change);
    return () => query.removeEventListener('change', change);
  }, []);
  return narrow;
}

export function CreatorThreads({campaign}: {campaign: BrandCampaign}) {
  const {data, reload, toast} = useBrand();
  // Unread first, then the latest conversation: the thread that needs an answer is the one that opens.
  const lastAt = (accountId: string) => (data?.messages || []).reduce((latest, message) => (message.project_id === campaign.id && message.account_id === accountId && message.created_at > latest ? message.created_at : latest), '');
  const people = [...campaign.participants].sort((a, b) => (b.unread_count - a.unread_count) || lastAt(b.account_id).localeCompare(lastAt(a.account_id)) || a.name.localeCompare(b.name));
  const [selected, setSelected] = useState(people[0]!.account_id);
  // On a phone the list comes first, and a creator picked from it opens their conversation.
  const [reading, setReading] = useState(false);
  const narrow = useNarrow();
  const showing = !narrow || reading;
  // A draft per creator, so switching threads never sends one creator's words to another.
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [fileDrafts, setFileDrafts] = useState<Record<string, File[]>>({});
  const [busy, setBusy] = useState(false);
  const [removing, setRemoving] = useState<BrandParticipant | null>(null);
  const person = campaign.participants.find((item) => item.account_id === selected) ?? people[0]!;
  const body = drafts[person.account_id] ?? '';
  const pendingFiles = fileDrafts[person.account_id] ?? [];
  // Their photos, earlier work and accounts, from the conversation's head.
  const profile = useCreatorProfile();
  const messages = (data?.messages || []).filter((m) => m.project_id === campaign.id && m.account_id === person.account_id);
  const root = useRef<HTMLDivElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const field = useRef<HTMLTextAreaElement>(null);
  const picker = useRef<HTMLInputElement>(null);
  /** A creator can attach the brief they are asking about; name the campaign it belongs to. */
  const briefCard = (jobId: string) => { const title = data?.campaigns.find((item) => item.job_id === jobId)?.title; return title ? `About the brief: ${title}` : 'About the brief'; };

  useEffect(() => {
    if (!showing || !person.unread_count) return;
    void markRead(person.account_id, campaign.id).then(() => reload(true));
  }, [showing, person.unread_count, person.account_id, campaign.id, reload]);

  // On a phone, the conversation picked from the list fills the screen between the bars.
  useEffect(() => {
    if (narrow && reading) root.current?.scrollIntoView({block: 'start', behavior: 'smooth'});
  }, [narrow, reading, person.account_id]);

  // The newest message in view: when a thread opens, and when a message arrives.
  useLayoutEffect(() => {
    const box = list.current;
    if (box) box.scrollTop = box.scrollHeight;
  }, [person.account_id, messages.length, showing]);

  // The box as tall as its text; its max-height (threads.css) stops it at six lines, and then it scrolls.
  // A conversation read to its end stays at its end while the box grows.
  useLayoutEffect(() => {
    const box = field.current;
    if (!box) return;
    const feed = list.current;
    const atEnd = feed ? feed.scrollHeight - feed.scrollTop - feed.clientHeight < 24 : false;
    box.style.height = 'auto';
    box.style.height = `${box.scrollHeight + box.offsetHeight - box.clientHeight}px`;
    if (feed && atEnd) feed.scrollTop = feed.scrollHeight;
  }, [body, showing]);

  function open(accountId: string) {
    setSelected(accountId);
    setReading(true);
  }

  async function submit(event?: FormEvent) {
    event?.preventDefault();
    const text = body.trim();
    const files = [...pendingFiles];
    if ((!text && files.length === 0) || busy) return;
    const to = person.account_id;
    setBusy(true);
    try {
      const sent = await sendMessage(to, campaign.id, text || `Shared ${files.length === 1 ? 'a file' : `${files.length} files`}.`);
      setDrafts((all) => ({...all, [to]: ''}));
      if (sent.held) {
        toast(files.length ? `${HELD_TOAST} The selected files were not sent and are still ready here.` : HELD_TOAST);
      } else {
        const failed: string[] = [];
        for (const file of files) {
          try { await uploadMessageFile(sent.message.id, file); }
          catch (error) { failed.push(`${file.name}: ${describe(error)}`); }
        }
        setFileDrafts((all) => ({...all, [to]: []}));
        if (failed.length) toast(failed.join(' '), true);
      }
      await reload(true);
    } catch (error) {
      toast(describe(error), true);
    } finally {
      setBusy(false);
    }
  }

  function chooseFiles(event: ChangeEvent<HTMLInputElement>) {
    const selectedFiles = [...(event.target.files || [])];
    event.target.value = '';
    if (!selectedFiles.length) return;
    const acceptable: File[] = [];
    for (const file of selectedFiles) {
      if (file.size > MAX_FILE_BYTES) toast(`${file.name} is larger than 100 MB.`, true);
      else acceptable.push(file);
    }
    const current = fileDrafts[person.account_id] ?? [];
    if (current.length + acceptable.length > MAX_ATTACHMENTS) toast(`Add up to ${MAX_ATTACHMENTS} files to one message.`, true);
    setFileDrafts((all) => {
      const next = [...(all[person.account_id] ?? []), ...acceptable].slice(0, MAX_ATTACHMENTS);
      return {...all, [person.account_id]: next};
    });
  }

  function removeFile(index: number) {
    setFileDrafts((all) => ({...all, [person.account_id]: (all[person.account_id] ?? []).filter((_, at) => at !== index)}));
  }

  async function removeCreator() {
    if (!removing || busy) return;
    setBusy(true);
    try {
      await removeCampaignCreator(campaign.id, removing.account_id);
      toast(`${removing.name} was removed from the campaign.`);
      setRemoving(null);
      await reload(true);
    } catch (error) {
      toast(describe(error), true);
      await reload(true);
    } finally {
      setBusy(false);
    }
  }

  const filesFor = (messageId: string): StoredFile[] => (data?.attachments || []).filter((file) => file.message_id === messageId);

  function onKey(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key !== 'Enter' || event.shiftKey || event.nativeEvent.isComposing) return;
    // A phone's keyboard has no Shift+Enter: there Enter is a new line, and the button sends.
    if (window.matchMedia('(pointer: coarse)').matches) return;
    event.preventDefault();
    void submit();
  }

  const status = (item: BrandParticipant) => participantStatus(item.reservation_status);
  return (
    <>
    <div className={`creator-chat${reading ? ' reading' : ''}`} ref={root}>
      <aside aria-label="Creators">
        {people.map((item) => (
          <button className={item.account_id === person.account_id ? 'active' : ''} key={item.account_id} type="button" onClick={() => open(item.account_id)}>
            <Person name={item.name} handle={item.handle} status={status(item)} sources={creatorPicture(item)} />
            {item.unread_count ? <i aria-label={`${item.unread_count} unread`}>{item.unread_count}</i> : null}
          </button>
        ))}
      </aside>
      <div className="chat-panel">
        <div className="chat-head">
          <button className="chat-back" type="button" onClick={() => setReading(false)} aria-label="All conversations"><Icon name="arrowLeft" size={18} /></button>
          <Person name={person.name} handle={person.handle} status={status(person)} sources={creatorPicture(person)} onOpen={() => profile.open(person.account_id)} />
          <button className="text-button chat-profile" type="button" onClick={() => profile.open(person.account_id)}>View profile</button>
          <button className="text-button chat-remove" type="button" onClick={() => setRemoving(person)}><Icon name="x" size={14} /><span>Remove</span></button>
        </div>
        <div className="messages" ref={list}>
          {messages.length ? messages.map((message) => (
            <article className={[message.sender === 'brand' ? 'mine' : '', message.guard ? `guarded ${message.guard}` : ''].filter(Boolean).join(' ')} key={message.id}>
              {message.brief_job_id ? <span className="message-brief"><Icon name="file" size={14} />{briefCard(message.brief_job_id)}</span> : null}
              {message.body ? <p>{message.body}</p> : null}
              <MessageFiles messageId={message.id} files={filesFor(message.id)} />
              <small>{new Date(message.created_at).toLocaleString(undefined, {day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'})}{message.guard ? ` · ${GUARD_LABEL[message.guard]}` : message.sender === 'brand' && message.read_by_creator_at ? ' · seen' : ''}</small>
            </article>
          )) : <div className="chat-empty"><Icon name="chat" /><b>No messages yet</b><span>Introduce the campaign or answer the creator’s first question here.</span></div>}
        </div>
        <form className="chat-composer" onSubmit={(event) => void submit(event)}>
          {pendingFiles.length ? <ul className="chat-pending-files">{pendingFiles.map((file, index) => (
            <li key={`${file.name}-${file.lastModified}-${index}`}><Icon name="paperclip" size={14} /><span>{file.name}</span><small>{humanSize(file.size)}</small>
              <button type="button" onClick={() => removeFile(index)} aria-label={`Remove ${file.name}`}><Icon name="x" size={13} /></button></li>
          ))}</ul> : null}
          <div className="chat-compose-row">
            <input ref={picker} type="file" multiple hidden onChange={chooseFiles} />
            <button className="chat-attach" type="button" disabled={busy} onClick={() => picker.current?.click()} aria-label="Attach files"><Icon name="paperclip" size={18} /></button>
            <textarea ref={field} rows={1} wrap="soft" value={body} maxLength={4000}
              onChange={(event) => setDrafts((all) => ({...all, [person.account_id]: event.target.value}))} onKeyDown={onKey}
              placeholder={`Write to ${person.name}…`} aria-label={`Message to ${person.name}`} />
            <button className="btn btn-g chat-send" type="submit" disabled={busy || (!body.trim() && pendingFiles.length === 0)} aria-label="Send"><Icon name="send" size={17} /></button>
          </div>
        </form>
      </div>
      {profile.panel}
    </div>
    {removing ? <RemoveCreatorDialog creator={removing} busy={busy} onCancel={() => setRemoving(null)} onConfirm={() => void removeCreator()} /> : null}
    </>
  );
}

/** Images show in the bubble; every other attachment downloads with its original name. */
function MessageFiles({messageId, files}: {messageId: string; files: StoredFile[]}) {
  if (!files.length) return null;
  return <div className="chat-message-files">{files.map((file) => {
    const url = messageFileUrl(messageId, file.id);
    return isImage(file.content_type)
      ? <a className="chat-message-image" href={url} target="_blank" rel="noreferrer" key={file.id}><img src={url} alt={file.name} loading="lazy" /></a>
      : <a className="chat-message-file" href={`${url}?download=1`} key={file.id}><Icon name="paperclip" size={15} /><span>{file.name}</span><small>{humanSize(file.size)}</small></a>;
  })}</div>;
}
